/**
 * WebSocket Type Definitions
 *
 * Types for AWS AppSync Events WebSocket integration.
 * Validation types are re-exported from validations/websocket.ts
 * Config and internal types are defined here.
 */

import type { WebSocketEvent as WebSocketEventType } from '@/validations/websocket';

// ------------- Re-export validated types from Zod schemas -------------
export type {
  ActivityData,
  ActivityInitialJob,
  ActivityKind,
  ActivityMeta,
  ActivityService,
  ActivityStatus,
  ActivityUpdatedEvent,
  AppSyncConnectionAckMessage,
  AppSyncConnectionErrorMessage,
  AppSyncConnectionInitMessage,
  AppSyncDataMessage,
  AppSyncErrorMessage,
  AppSyncEventMessage,
  AppSyncIncomingMessage,
  AppSyncKeepAliveMessage,
  AppSyncMessageType,
  AppSyncOutgoingMessage,
  AppSyncSubscribeErrorMessage,
  AppSyncSubscribeMessage,
  AppSyncSubscribeSuccessMessage,
  AppSyncUnsubscribeMessage,
  WebSocketEvent,
} from '@/validations/websocket';

// Re-export validation functions
export { parseEventData, parseIncomingMessage } from '@/validations/websocket';

// Re-export schemas for consumers that need them
export {
  ActivityDataSchema,
  ActivityInitialJobSchema,
  ActivityKindSchema,
  ActivityMetaSchema,
  ActivityServiceSchema,
  ActivityStatusSchema,
  ActivityUpdatedEventSchema,
  AppSyncIncomingMessageSchema,
  WebSocketEventSchema,
} from '@/validations/websocket';

// Re-export enum value constants for type-safe comparisons
export {
  ActivityKindValues,
  ActivityServiceValues,
  ActivityStatusValues,
  AppSyncMessageTypeValues,
} from '@/validations/websocket';

// ------------- Connection State -------------

/**
 * WebSocket connection state enum
 */
export enum WebSocketConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

// ------------- Subscription Types -------------

/**
 * Subscription options for subscribing to a channel
 */
export type SubscriptionOptions = {
  channel: string;
  onEvent: (event: WebSocketEventType) => void;
  onError?: (error: Error) => void;
};

/**
 * Subscription info stored internally
 */
export type SubscriptionInfo = SubscriptionOptions & {
  id: string;
};

// ------------- Service Types -------------

/**
 * WebSocket service configuration
 */
export type WebSocketServiceConfig = {
  url: string;
  getAuthToken: () => Promise<string>;
  onConnectionStateChange?: (state: WebSocketConnectionState) => void;
  onError?: (error: Error) => void;
};

/**
 * Reconnection configuration
 */
export type ReconnectionConfig = {
  initialDelay: number;
  maxDelay: number;
  maxAttempts: number;
};

// ------------- Pending Subscription -------------

/**
 * Pending subscription with pre-generated ID
 * Used to ensure subscription IDs are consistent across connect/reconnect cycles
 */
export type PendingSubscription = SubscriptionOptions & {
  id: string;
};

// ------------- Error Context -------------

/**
 * Enhanced error context for WebSocket errors
 * Provides detailed information for debugging connection issues
 */
export type WebSocketErrorContext = {
  closeCode: number | null;
  closeReason: string;
  reconnectAttempt: number;
  timestamp: string;
};
